import { ApolloLink, Observable } from '@apollo/client';

import { isExpectedSessionEndError } from '@/adapters/auth/tokenRefreshManager';
import {
  isTransientGraphQLError,
  isTransientServerError,
  TRANSIENT_ERROR_BREADCRUMB,
} from '@/adapters/graphql/transientError';
import { isUnauthenticatedError } from '@/adapters/graphql/unauthenticatedError';
import { sanitizeObject } from '@/adapters/logger/sanitizer';
import type { Logger } from '@/adapters/logger/types';

const SESSION_ENDED_BREADCRUMB = 'Session ended, error suppressed from Sentry';

export const createErrorLink = (logger: Logger) =>
  new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          if (result.errors) {
            result.errors.forEach((error) => {
              if (isUnauthenticatedError(error)) {
                logger.addBreadcrumb(SESSION_ENDED_BREADCRUMB, {
                  operationName: operation.operationName,
                });

                return;
              }

              if (isTransientGraphQLError(error)) {
                logger.addBreadcrumb(TRANSIENT_ERROR_BREADCRUMB, {
                  operationName: operation.operationName,
                  variables: sanitizeObject(operation.variables),
                });

                return;
              }

              const sanitizedExtensions = error.extensions
                ? sanitizeObject(error.extensions as Record<string, unknown>)
                : undefined;

              logger.captureException(new Error(error.message), {
                operationName: operation.operationName,
                variables: sanitizeObject(operation.variables),
                extensions: sanitizedExtensions,
              });
            });
          }
          observer.next(result);
        },
        error: (error) => {
          if (isExpectedSessionEndError(error)) {
            logger.addBreadcrumb(SESSION_ENDED_BREADCRUMB, {
              operationName: operation.operationName,
            });
            observer.error(error);
            return;
          }

          if (isTransientServerError(error)) {
            logger.addBreadcrumb('GraphQL server error suppressed from Sentry', {
              operationName: operation.operationName,
              statusCode: error.statusCode,
              variables: sanitizeObject(operation.variables),
            });
            observer.error(error);
            return;
          }

          logger.captureException(error, {
            operationName: operation.operationName,
            variables: sanitizeObject(operation.variables),
          });
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });

      return () => {
        if (subscription) subscription.unsubscribe();
      };
    });
  });
